import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Delay (ms) before the skeleton becomes visible. Avoids flash on fast loads. Default 200ms. */
  delay?: number;
}

/**
 * Skeleton placeholder using the "static-then-shimmer" pattern adopted by
 * LinkedIn, Vercel, Linear and GitHub:
 *  - Renders nothing for the first 200ms (most loads finish in that window)
 *  - Fades in a flat, low-contrast surface
 *  - A single GPU-accelerated gradient sweeps once every 1.6s
 *  - Honors prefers-reduced-motion
 */
function Skeleton({ className, delay = 120, ...props }: SkeletonProps) {
  const [visible, setVisible] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) return;
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      className={cn(
        "skeleton-base rounded-md",
        className,
      )}
      {...props}
    />
  );
}

export { Skeleton };
